import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { LuCalendarCheck, LuHouse, LuStethoscope, LuUsersRound } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { fetchAccessiblePatients, getVisitedPatientIds } from '../../utils/patientAccess';
import { useTranslation } from '../../i18n/LanguageContext';
import { PATIENT_STATUSES, PATIENT_STATUS_DOTS, buildPatientSummary } from './projectHelpers';

const tileClass = 'tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.04]';

/** Patient and visit totals of one employee in one project ("Detay" dialog). */
const ProjectPatientSummary = ({ projectId, employeeId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [patientSummary, setPatientSummary] = useState(null);
  const [visitCount, setVisitCount] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSummary = async () => {
      setLoading(true);
      setFailed(false);
      try {
        // Bu modal tek bir çalışanın kaydıdır: hem hastalar hem ziyaretler
        // yalnızca bu çalışana aittir, projenin tamamı değil. Hasta kapsamı
        // projenin ortak kuralıdır (sahip olduğu + ziyaret ettiği hastalar).
        const visitsSnap = await getDocs(
          query(collection(db, 'Visits'), where('projectId', '==', projectId), where('employeeId', '==', employeeId))
        );
        if (cancelled) return;

        const visits = visitsSnap.docs.map((d) => d.data());
        const patients = await fetchAccessiblePatients(projectId, employeeId, {
          visitedPatientIds: getVisitedPatientIds(visits, projectId, employeeId),
        });
        if (cancelled) return;

        setPatientSummary(buildPatientSummary(patients));
        setVisitCount(visits.length);
      } catch (err) {
        console.error('Error fetching project summary:', err);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [projectId, employeeId]);

  const stats = [
    { key: 'patients', icon: LuUsersRound, value: patientSummary?.total ?? 0 },
    { key: 'homeVisitPatients', icon: LuHouse, value: patientSummary?.homeVisit ?? 0 },
    { key: 'visits', icon: LuCalendarCheck, value: visitCount },
  ];

  return (
    <section>
      <h3 className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
        <LuStethoscope aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-500" />
        {t('projectDetail.summaryTitle')}
        <span className="tw-text-xs tw-font-normal tw-text-slate-500 dark:tw-text-slate-400">· {t('projectDetail.summaryHint')}</span>
      </h3>

      {loading ? (
        <div className="tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.key} className="tw-h-20 tw-animate-pulse tw-rounded-2xl tw-bg-slate-100 dark:tw-bg-white/[0.05]" />
          ))}
        </div>
      ) : failed ? (
        <p className="tw-mt-3 tw-rounded-2xl tw-bg-rose-50 tw-px-4 tw-py-3 tw-text-sm tw-text-rose-700 dark:tw-bg-rose-500/10 dark:tw-text-rose-300">
          {t('projectDetail.summaryError')}
        </p>
      ) : (
        <>
          <div className="tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-3">
            {stats.map(({ key, icon: Icon, value }) => (
              <div key={key} className={tileClass}>
                <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
                  <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {t(`projectDetail.${key}`)}
                </p>
                <p className="tw-mt-1 tw-text-2xl tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{value}</p>
              </div>
            ))}
          </div>

          <ul className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
            {Object.keys(PATIENT_STATUSES).map((key) => (
              <li key={key} className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/80 tw-px-3 tw-py-2 dark:tw-border-white/10">
                <span aria-hidden="true" className={`tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full ${PATIENT_STATUS_DOTS[key]}`} />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
                  {t(`projectDetail.patientStatus.${key}`)}
                </span>
                <span className="tw-text-sm tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">
                  {patientSummary?.byStatus[key] || 0}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

ProjectPatientSummary.propTypes = {
  projectId: PropTypes.string.isRequired,
  employeeId: PropTypes.string,
};

export default ProjectPatientSummary;
